import { getEduDemoDataset, getSchoolDemoDataset } from './eduDemoData'
import type { EduDemoDataset } from '../../explore_seeds/edu/eduDemoTypes'
import type {
  EduAttendance,
  EduEnrollment,
  EduRoom,
  EduSession,
  EduSessionSeries,
  EduSettings,
  EduSubmission,
  EduYearHoliday,
  EduYearSettings,
} from '../../types/schema'
import type { EduLearnerContactDoc, EduLearnerDoc } from '../eduLearnerService'
import type { EduAssessment, EduMark } from '../eduGradingService'
import type { EduLeaveRequestDoc, ListLeaveForClassInput } from '../eduLeaveService'
import type { HomeworkChecklistDoc } from '../eduHomeworkService'

async function resolveDataset(workspaceIdOrRootId?: string): Promise<EduDemoDataset> {
  let ws = workspaceIdOrRootId
  if (ws?.startsWith('prog-')) ws = 'sports-academy-demo'
  else if (ws?.startsWith('year-jee-neet')) ws = 'coaching-demo'
  else if (ws?.startsWith('year-')) ws = 'school-demo'

  if (ws) {
    const ds = await getEduDemoDataset(ws)
    if (ds) return ds
  }
  return getSchoolDemoDataset()
}

// ---------------------------------------------------------------------------
// 1. Attendance
// ---------------------------------------------------------------------------

export async function demoListAttendanceForLearner(
  workspaceId: string,
  learnerId: string,
  from: string,
  to: string,
): Promise<EduAttendance[]> {
  const ds = await resolveDataset(workspaceId)
  return ds.attendance.filter(
    (a) => a.learnerId === learnerId && a.date >= from && a.date <= to,
  )
}

export async function demoListAttendanceForClass(
  workspaceId: string,
  classId: string,
  from: string,
  to: string,
): Promise<EduAttendance[]> {
  const ds = await resolveDataset(workspaceId)
  return ds.attendance.filter(
    (a) => a.classId === classId && a.date >= from && a.date <= to,
  )
}

// ---------------------------------------------------------------------------
// 2. Grading
// ---------------------------------------------------------------------------

export async function demoListAssessmentsForSubject(
  workspaceId: string,
  nodeId: string,
  options?: { includeArchived?: boolean; restrictedUid?: string },
): Promise<EduAssessment[]> {
  const ds = await resolveDataset(workspaceId)
  return ds.assessments.filter((a) => {
    if (a.nodeId !== nodeId) return false
    if (!options?.includeArchived && a.status === 'ARCHIVED') return false
    if (
      options?.restrictedUid &&
      (!Array.isArray(a.readAccess) || !a.readAccess.includes(options.restrictedUid))
    ) {
      return false
    }
    return true
  })
}

export async function demoListDueAssessmentsForWorkspace(
  workspaceId: string,
  fromDate: string,
  toDate: string,
  options?: { restrictedUid?: string },
): Promise<EduAssessment[]> {
  const ds = await resolveDataset(workspaceId)
  return ds.assessments.filter((a) => {
    if (a.status !== 'ACTIVE') return false
    if (!a.date || a.date < fromDate || a.date > toDate) return false
    if (
      options?.restrictedUid &&
      (!Array.isArray(a.readAccess) || !a.readAccess.includes(options.restrictedUid))
    ) {
      return false
    }
    return true
  })
}

export async function demoGetAssessment(
  workspaceId: string,
  id: string,
): Promise<EduAssessment | null> {
  const ds = await resolveDataset(workspaceId)
  const found = ds.assessments.find((a) => a.id === id)
  return found ?? null
}

export async function demoGetMark(
  workspaceId: string,
  assessmentId: string,
  learnerId: string,
): Promise<EduMark | null> {
  const ds = await resolveDataset(workspaceId)
  const key = `${assessmentId}_${learnerId}`
  return ds.marks[key] ?? null
}

// ---------------------------------------------------------------------------
// 3. Leave
// ---------------------------------------------------------------------------

export async function demoListLeaveForClass(
  input: ListLeaveForClassInput,
): Promise<EduLeaveRequestDoc[]> {
  const ds = await resolveDataset(input.workspaceId)
  return ds.leaveRequests.filter((lr) => {
    if (!lr.classIds.includes(input.classId)) return false
    if (input.status && lr.status !== input.status) return false
    return true
  })
}

// ---------------------------------------------------------------------------
// 4. Homework
// ---------------------------------------------------------------------------

export async function demoListHomeworkForClass(
  workspaceId: string,
  subjectIds: string[],
): Promise<HomeworkChecklistDoc[]> {
  const ds = await resolveDataset(workspaceId)
  return ds.homework.filter((hw) => subjectIds.includes(hw.parentContainerId))
}

export async function demoListSubmissionsForClass(
  workspaceId: string,
  classId: string,
): Promise<EduSubmission[]> {
  const ds = await resolveDataset(workspaceId)
  return ds.submissions.filter((s) => s.classId === classId)
}

export async function demoListSubmissionsForHomework(
  workspaceId: string,
  checklistId: string,
): Promise<EduSubmission[]> {
  const ds = await resolveDataset(workspaceId)
  return ds.submissions.filter((s) => s.checklistId === checklistId)
}

export async function demoListSubmissionsForLearner(
  workspaceId: string,
  learnerId: string,
  from?: string,
  to?: string,
): Promise<EduSubmission[]> {
  const ds = await resolveDataset(workspaceId)
  return ds.submissions.filter((s) => {
    if (s.learnerId !== learnerId) return false
    const dateStr = typeof s.submittedAt === 'string' ? s.submittedAt : ''
    if (from && dateStr && dateStr < from) return false
    if (to && dateStr && dateStr > to) return false
    return true
  })
}

// ---------------------------------------------------------------------------
// 5. Learners & Enrolments
// ---------------------------------------------------------------------------

export async function demoListLearners(workspaceId: string): Promise<EduLearnerDoc[]> {
  const ds = await resolveDataset(workspaceId)
  return ds.learners
}

export async function demoListLearnersForMember(
  workspaceId: string,
  _uid: string,
): Promise<EduLearnerDoc[]> {
  void _uid
  const ds = await resolveDataset(workspaceId)
  return ds.learners
}

export async function demoGetLearner(
  workspaceId: string,
  learnerId: string,
): Promise<EduLearnerDoc | null> {
  const ds = await resolveDataset(workspaceId)
  return ds.learners.find((l) => l.id === learnerId) ?? null
}

export async function demoGetLearnerContact(
  learnerId: string,
): Promise<EduLearnerContactDoc | null> {
  for (const ws of ['school-demo', 'coaching-demo', 'sports-academy-demo']) {
    const ds = await getEduDemoDataset(ws)
    const contact = ds?.learnerContacts.get(learnerId)
    if (contact) return contact
  }
  const defaultDs = await getSchoolDemoDataset()
  return defaultDs.learnerContacts.get(learnerId) ?? null
}

export async function demoListActiveEnrolmentsForNodes(
  workspaceId: string,
  nodeIds: string[],
): Promise<EduEnrollment[]> {
  const ds = await resolveDataset(workspaceId)
  return ds.enrolments.filter((e) => nodeIds.includes(e.nodeId))
}

export async function demoListActiveEnrolmentsForLearners(
  workspaceId: string,
  learnerIds: string[],
): Promise<EduEnrollment[]> {
  const ds = await resolveDataset(workspaceId)
  return ds.enrolments.filter((e) => learnerIds.includes(e.learnerId))
}

// ---------------------------------------------------------------------------
// 6. Timetable
// ---------------------------------------------------------------------------

export async function demoGetEduSettings(workspaceId: string): Promise<EduSettings | null> {
  const ds = await resolveDataset(workspaceId)
  return ds.eduSettings
}

export async function demoGetEduYearSettings(rootId: string): Promise<EduYearSettings | null> {
  const ds = await resolveDataset(rootId)
  return ds.eduYearSettings
}

export async function demoGetYearHolidays(rootId: string): Promise<EduYearHoliday[]> {
  const ds = await resolveDataset(rootId)
  return ds.holidays
}

export async function demoListSeriesForRoot(
  workspaceId: string,
  rootId: string,
): Promise<EduSessionSeries[]> {
  const ds = await resolveDataset(workspaceId || rootId)
  return ds.sessionSeries.filter((s) => s.rootId === rootId)
}

export async function demoListSessionDocs(
  workspaceId: string,
  rootId: string,
  fromDate: string,
  toDate: string,
): Promise<EduSession[]> {
  const ds = await resolveDataset(workspaceId || rootId)
  return ds.sessions.filter(
    (s) => s.rootId === rootId && s.date >= fromDate && s.date <= toDate,
  )
}

export async function demoListSubstituteSessions(
  _workspaceId: string,
  _uid: string,
  _fromDate: string,
  _toDate: string,
): Promise<EduSession[]> {
  void _workspaceId
  void _uid
  void _fromDate
  void _toDate
  return []
}

export async function demoListSeriesForTeacher(
  workspaceId: string,
  uid: string,
): Promise<EduSessionSeries[]> {
  const ds = await resolveDataset(workspaceId)
  return ds.sessionSeries.filter((s) => s.teacherUids.includes(uid))
}

export async function demoListSeriesForWorkspace(workspaceId: string): Promise<EduSessionSeries[]> {
  const ds = await resolveDataset(workspaceId)
  return ds.sessionSeries
}

export async function demoListSessionDocsForSeries(
  workspaceId: string,
  seriesIds: string[],
  fromDate: string,
  toDate: string,
): Promise<EduSession[]> {
  const ds = await resolveDataset(workspaceId)
  return ds.sessions.filter(
    (s) => seriesIds.includes(s.seriesId) && s.date >= fromDate && s.date <= toDate,
  )
}

export async function demoListRooms(workspaceId: string): Promise<EduRoom[]> {
  const ds = await resolveDataset(workspaceId)
  return ds.rooms
}
